import { Component, inject, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { SYSTEM_MAINTAINANCE_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { ApiService } from 'src/app/services/api.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from 'src/app/core/services/auth.service';

@Component({
    selector: 'app-app-maintainance',
    templateUrl: './app-maintainance.component.html',
    styleUrl: './app-maintainance.component.scss'
})
export class AppMaintainanceComponent implements OnInit {
    authService = inject(AuthService);
    tabs = SYSTEM_MAINTAINANCE_TAB_OPTIONS;

    // maintainance switch options
    switchOptions = [
        { label: 'On', value: 'enable' },
        { label: 'Off', value: 'disable' },
    ]
    maintainanceType: any;
    maintenanceType_text: any;
    selectedMaintenanceText = '';
    private originalMaintainanceType: any;

    // check app version switch options
    checkAppVersionOptions = [
        { label: 'On', value: 1 },
        { label: 'Off', value: 0 },
    ]
    checkAppVersionFlag = 0;
    selectedCheckAppVersionText = '';
    private originalCheckAppVersionFlag = 0;

    constructor(
        private toastr: ToastrService,
        private apiService: ApiService,
        private spinner: NgxSpinnerService,
    ) {  }

    ngOnInit() {
        this.spinner.show().catch(() => console.log('Spinner error'));
        this.apiService.getMaintainanceStatus().subscribe({
            next: (response: any) => {
            if (response.status === 200) {
                const isOn = response.result.onMaintenanceMode;
                const matched = this.switchOptions.find(o => o.value === (isOn ? 'enable' : 'disable'));
                if (matched) {
                    this.maintainanceType = matched.value;
                    this.maintenanceType_text = matched.label;
                    this.selectedMaintenanceText = matched.label;
                    this.originalMaintainanceType = matched.value;
                }
            } else {
                this.toastr.error(response.error.message + '!', 'Something went wrong!');
            }
            },
            error: (error: any) => {
            this.toastr.error(error.error.message + '!', 'Something went wrong!');
            },
            complete: () => {
            this.spinner.hide().catch(() => console.log('Spinner error'));
            }
        });

        this.spinner.show().catch(() => console.log('Spinner error'));
        this.apiService.getCheckAppVersionFlag().subscribe({
            next: (response: any) => {
            if (response.status === 200) {
                const flag = Number(response.result.checkAppVersionFlag);
                const matched = this.checkAppVersionOptions.find(o => o.value === flag);
                if (matched) {
                    this.checkAppVersionFlag = matched.value;
                    this.selectedCheckAppVersionText = matched.label;
                    this.originalCheckAppVersionFlag = matched.value;
                }
            } else {
                this.toastr.error(response.error.message + '!', 'Something went wrong!');
            }
            },
            error: (error: any) => {
            this.toastr.error(error.error.message + '!', 'Something went wrong!');
            },
            complete: () => {
            this.spinner.hide().catch(() => console.log('Spinner error'));
            }
        });
        }

    submit() {
        this.spinner.show().catch(() => console.log('Spinner error'));
        this.apiService.updateMaintainanceMode(this.maintainanceType).subscribe({
            next: (response: any) => {
            if (response.status === 200) {
                this.toastr.success(response.message + '!', 'Success!');
                this.originalMaintainanceType = this.maintainanceType;
            } else {
                this.toastr.error(response.error.message + '!', 'Something went wrong!');
            }
            },
            error: (error: any) => {
            this.toastr.error(error.error.message + '!', 'Something went wrong!');
            this.spinner.hide().catch(() => console.log('Spinner error'));
            },
            complete: () => {
            this.spinner.hide().catch(() => console.log('Spinner error'));
            }
        });
    }

    submitCheckAppVersion() {
        this.spinner.show().catch(() => console.log('Spinner error'));
        this.apiService.updateCheckAppVersionFlag({ checkAppVersionFlag: this.checkAppVersionFlag }).subscribe({
            next: (response: any) => {
            if (response.status === 200) {
                this.toastr.success(response.message + '!', 'Success!');
                this.originalCheckAppVersionFlag = this.checkAppVersionFlag;
            } else {
                this.toastr.error(response.error.message + '!', 'Something went wrong!');
            }
            },
            error: (error: any) => {
            this.toastr.error(error.error.message + '!', 'Something went wrong!');
            this.spinner.hide().catch(() => console.log('Spinner error'));
            },
            complete: () => {
            this.spinner.hide().catch(() => console.log('Spinner error'));
            }
        });
    }

    setMaintainanceMode(text: string, value: any){
        this.maintainanceType = value;
        this.maintenanceType_text = text;
    }

    setCheckAppVersionFlag(text: string, value: any){
        this.checkAppVersionFlag = value;
        this.selectedCheckAppVersionText = text;
    }

    // ---- Tooltips (reflect current on/off status) ----

    get getMaintenanceTooltip(): string {
        return `This control Megapay's maintenance mode. If turned on, all Megapay affiliated applications would be under maintenance.`;
    }

    get getCheckAppVersionTooltip(): string {
        return `This controls Megapay Check App Version that when turned on, forces Megapay App users to update their app if outdated.`;
    }

    get getMaintenanceSubmitTooltip(): string {
        const adminState = this.getAdminButtonState();
        if (adminState.disabled) {
            return adminState.tooltip;
        }
        if (!this.isMaintenanceChanged()) {
            return 'No changes detected. Please modify the switch before submitting.';
        }
        return '';
    }

    get getCheckAppVersionSubmitTooltip(): string {
        const adminState = this.getAdminButtonState();
        if (adminState.disabled) {
            return adminState.tooltip;
        }
        if (!this.isCheckAppVersionChanged()) {
            return 'No changes detected. Please modify the switch before submitting.';
        }
        return '';
    }

    // ---- Change detection (submit disabled unless value changed) ----

    isMaintenanceChanged(): boolean {
        return this.maintainanceType !== this.originalMaintainanceType;
    }

    isCheckAppVersionChanged(): boolean {
        return this.checkAppVersionFlag !== this.originalCheckAppVersionFlag;
    }

    // ---- Admin gating ----

    getAdminButtonState(): { disabled: boolean; tooltip: string } {
        if (!this.authService.isAdmin()) {
            return {
                disabled: true,
                tooltip: 'You are not allowed to change details of maintanance. Please contact an Admin if you need assistance',
            };
        }
        return { disabled: false, tooltip: '' };
    }

    isMaintenanceSubmitDisabled(): boolean {
        return this.getAdminButtonState().disabled || !this.isMaintenanceChanged();
    }

    isCheckAppVersionSubmitDisabled(): boolean {
        return this.getAdminButtonState().disabled || !this.isCheckAppVersionChanged();
    }
}
